import * as Api from '@ViewModels';

export type FanOutWarningSeverity = 'danger' | 'soft';

export interface IFanOutWarning {
	severity: FanOutWarningSeverity;
	stepType: string;
	message: string;
}

export type RenewalFanOutRole =
	| typeof Api.RenewalSendFromMode.PolicyOwner
	| typeof Api.RenewalSendFromMode.PolicyCsr
	| typeof Api.RenewalSendFromMode.PolicyProducer;

export function getRenewalFanOutRoleLabel(role: RenewalFanOutRole): string {
	switch (role) {
		case Api.RenewalSendFromMode.PolicyOwner:
			return 'Policy Owner';
		case Api.RenewalSendFromMode.PolicyCsr:
			return 'Policy CSR';
		case Api.RenewalSendFromMode.PolicyProducer:
			return 'Policy Producer';
		default:
			return 'Policy Owner';
	}
}

const TRIGGER_AUTOMATION_STEP_TYPE = 'TriggerAutomationStep';

function getStepWarning(step: Api.IAutomationStep | undefined, roleLabel: string): IFanOutWarning | null {
	if (!step) {
		return null;
	}

	const apiModelType = (step as { _type?: string })._type;

	if (apiModelType === TRIGGER_AUTOMATION_STEP_TYPE) {
		return {
			severity: 'soft',
			stepType: TRIGGER_AUTOMATION_STEP_TYPE,
			message: `The triggered automation will start once for each ${roleLabel}. If a contact has multiple renewing policies with multiple different ${roleLabel}s, the triggered automation may run multiple times for that contact. Make sure the triggered automation has the appropriate cooldown periods so that it behaves as expected.`,
		};
	}

	switch (step._type) {
		case 'HandwrittenCardAutomationStep':
			return {
				severity: 'danger',
				stepType: Api.AutomationStepType.HandwrittenCard,
				message: `This template sends a handwritten card. A contact with multiple policies will receive one card per ${roleLabel} — so a contact might receive multiple handwritten cards.`,
			};
		case 'AddTagAutomationStep':
		case 'RemoveTagAutomationStep':
			return {
				severity: 'soft',
				stepType: Api.AutomationStepType.AddTag,
				message: `Tagging will happen once for each ${roleLabel}. If a contact has multiple renewing policies with multiple different ${roleLabel}s and this tag starts other automations, those automations may run multiple times. Make sure any of your tag-triggered automations have the appropriate cooldown periods so that they behave as expected.`,
			};
		case 'OpportunityAutomationStep':
			return {
				severity: 'soft',
				stepType: Api.AutomationStepType.Opportunity,
				message: `A new opportunity will be created for each ${roleLabel}. If a contact has multiple renewing policies with multiple different ${roleLabel}s, multiple opportunities will be created for that contact — one per ${roleLabel}.`,
			};
		default:
			return null;
	}
}

export function getRenewalFanOutWarnings(
	steps: Api.IAutomationStep[] | undefined,
	role: RenewalFanOutRole | null | undefined
): IFanOutWarning[] {
	if (!role || !steps?.length) {
		return [];
	}

	const roleLabel = getRenewalFanOutRoleLabel(role);
	const seenStepTypes = new Set<string>();
	const warnings: IFanOutWarning[] = [];

	for (const step of steps) {
		const warning = getStepWarning(step, roleLabel);
		if (warning && !seenStepTypes.has(warning.stepType)) {
			seenStepTypes.add(warning.stepType);
			warnings.push(warning);
		}
	}

	return warnings;
}

export function isRenewalFanOutRole(mode: Api.IRenewalSendFromMode | null | undefined): mode is RenewalFanOutRole {
	return (
		mode === Api.RenewalSendFromMode.PolicyOwner ||
		mode === Api.RenewalSendFromMode.PolicyCsr ||
		mode === Api.RenewalSendFromMode.PolicyProducer
	);
}

export function sendEmailFromToRenewalFanOutRole(
	mode: Api.ISendEmailFrom | null | undefined
): RenewalFanOutRole | null {
	switch (mode) {
		case Api.SendEmailFrom.PolicyOwner:
			return Api.RenewalSendFromMode.PolicyOwner;
		case Api.SendEmailFrom.PolicyCsr:
			return Api.RenewalSendFromMode.PolicyCsr;
		case Api.SendEmailFrom.PolicyProducer:
			return Api.RenewalSendFromMode.PolicyProducer;
		default:
			return null;
	}
}
